import { 
	createSlice,
	createSelector,
	createAsyncThunk,
	createEntityAdapter
} from '@reduxjs/toolkit'

import {
	envAwarePost,
	dateToAPIStandard,
} from '../../util/util'

import {dateToYMD} from '../../util/datetime'

export const getAggregatedMetricProgress = createAsyncThunk(
	'getAggregatedMetricProgress',
	async (months) => await envAwarePost(
		'api/metrics/',
		{action:'getAggregatedMetricProgress',months}
	)
)

export const getAllProgressData = createAsyncThunk(
	'getAllProgressData',
	async client_id => await envAwarePost(
		'api/metrics/',
		{action:'getAllProgressData',client_id}
	)
)

export const logMetricProgress = createAsyncThunk(
	'logMetricProgress',
	async (payload,{dispatch}) => {
		const res =await envAwarePost(
			'api/metrics/',
			{action:'logMetricProgress',...payload}
		)
		dispatch(getAggregatedMetricProgress(3))
		return res
	}
)

export const editMetricStat = createAsyncThunk(
	'editMetricStat',
	async (payload,{dispatch}) => {
		const res = await envAwarePost(
			'api/metrics/',
			{action:'editMetricStat',...payload}
		)
		dispatch(getAggregatedMetricProgress(3))
		return res
	}
)

export const deleteProgressStat = createAsyncThunk(
	'deleteProgressStat',
	async (payload,{dispatch}) => {
		const res = await envAwarePost(
			'api/metrics/',
			{action:'deleteProgressStat',stat_id:payload.id}
		)
		dispatch(getAggregatedMetricProgress(3))
		return res
	}
)

const progressAdapter = createEntityAdapter({
	selectId: ent => ent.metric_id
})

const aiLogAdapter = createEntityAdapter()

const slice = createSlice({
	name: 'metrics',
	initialState : {
		progress:progressAdapter.getInitialState({
			status:'init',
			clientId:null,
			filters: {
				category:'all',
				//-1 is value for displaying all data
				//if this is a positive value charts/tables
				//will only display data for todaysDate - daysBack
				daysBack:-1
			}
		}),
		aggregates:{
			status:'init',
			data:null,
			filters:{
				months:3
			}
		},
		aiLogs:aiLogAdapter.getInitialState(),
		addDataModal : {
			show:false,
			//default to today
			date:dateToYMD(new Date())
		}
	},
	reducers : {
		setAddDataModal: (state,action) => {
			state.addDataModal = {
				...state.addDataModal,
				...action.payload
			}
		},
		setFilters:(state,action)=>{
			state.progress.filters = {
				...state.progress.filters,
				...action.payload
			}
		},
		reset:(state,action)=>{
			state.progress = {
				...state.progress,
				status:'init',
				clientId:null,
				filters: {
					category:'all',
					//-1 is value for displaying all data
					//if this is a positive value charts/tables
					//will only display data for todaysDate - daysBack
					daysBack:-1
				}
			}
		}
	},

	extraReducers : {
		[getAllProgressData.pending] : (state,action) => { 
			const clientId = action.meta.arg
			state.progress.clientId = clientId
			state.progress.status = 'pending'
		},

		[getAllProgressData.fulfilled] : (state,action) => { 
			state.progress.status = 'idle' 
			const cleanedData = action.payload.data.metrics.filter( 
				m => Boolean(m.metric_id) 
			)
			progressAdapter.setAll(state.progress,cleanedData)
			aiLogAdapter.setAll(state.aiLogs,action.payload.data.ai_logs)
		},

		[logMetricProgress.fulfilled] : (state,action)=>{
			const {success} = action.payload
			if ( success === '0' ) return 
			const {stat} = action.payload.data
			const metric = state.progress.entities[stat.metric_id]
			//filter out the old data before adding the new stat data
			metric.stats = metric.stats.filter(s=>parseInt(s.stats_id)!==parseInt(stat.stats_id))
			metric.stats.push(stat)
			metric.stats.sort((a,b)=>a.date<=b.date?-1:1)
		},

		[editMetricStat.fulfilled] : (state,action)=>{
			const {success} = action.payload
			if ( success === '0' ) return 
			const {stat} = action.payload.data
			const metric = state.progress.entities[stat.metric_id]
			//filter out the old data before adding the new stat data
			metric.stats = metric.stats.filter(s=>parseInt(s.stats_id)!==parseInt(stat.stats_id))
			metric.stats.push(stat)
			metric.stats.sort((a,b)=>a.date<=b.date?-1:1)
		},

		[deleteProgressStat.pending] : (state,action)=>{
			const {metricId,id} = action.meta.arg
			const metric = state.progress.entities[metricId]
			metric.stats = metric.stats.filter(s=>parseInt(s.stats_id)!==parseInt(id))
		},

		[getAggregatedMetricProgress.pending] : (state,action)=>{
			state.aggregates.status = 'pending'
			state.aggregates.filters.months = action.meta.arg
		},
		[getAggregatedMetricProgress.fulfilled] : (state,action)=>{
			state.aggregates.status = 'idle'
			if ( parseInt(action.payload.success) === 1 ) { 

				state.aggregates.data = {
					improved:action.payload.data.total_improved_aggregates
				}
			}
		}

	}
})

export const {
	setAddDataModal,
	setFilters,
	reset
} = slice.actions

export const selectProgressClientId = state => state.allMetrics.progress.clientId

export const selectAddDataModal = state => state.allMetrics.addDataModal

export const selectProgressFilters = state => state.allMetrics.progress.filters

export const selectProgressStatus = state => state.allMetrics.progress.status

export const {
	selectAll: selectAllProgress,
	selectById: selectProgressById,
	selectIds: selectProgressIds
} = progressAdapter.getSelectors( state => state.allMetrics.progress )

export const {
	selectAll: selectAllAiLogs,
	selectById: selectAiLogById,
	selectIds: selectAiLogIds
} = aiLogAdapter.getSelectors( state => state.allMetrics.aiLogs )

export const selectMetricCategories = state => {
	const all = selectAllProgress(state)	
	const categoryMap = {}
	all.forEach(item=>{
		categoryMap[item.categorization.category_id] = item.categorization
	})

	return Object.values(categoryMap)
}



export default slice.reducer